import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const CreateQRCode = () => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [iosDeepLink, setIosDeepLink] = useState('');
  const [iosAppStoreUrl, setIosAppStoreUrl] = useState('');
  const [androidDeepLink, setAndroidDeepLink] = useState('');
  const [androidPlayStoreUrl, setAndroidPlayStoreUrl] = useState('');
  const [webUrl, setWebUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdQR, setCreatedQR] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/qr/create', {
        originalUrl: url,
        title,
        description,
        iosDeepLink: iosDeepLink || null,
        iosAppStoreUrl: iosAppStoreUrl || null,
        androidDeepLink: androidDeepLink || null,
        androidPlayStoreUrl: androidPlayStoreUrl || null,
        webUrl: webUrl || null,
      });

      setCreatedQR(response.data.qrCode);
      setUrl('');
      setTitle('');
      setDescription('');
      setIosDeepLink('');
      setIosAppStoreUrl('');
      setAndroidDeepLink('');
      setAndroidPlayStoreUrl('');
      setWebUrl('');
      setShowAdvanced(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create QR code');
    } finally {
      setLoading(false);
    }
  };

  if (createdQR) {
    return (
      <div className="container">
        <div className="card" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ 
            width: '64px', 
            height: '64px', 
            background: 'var(--gradient-primary)', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          
          <h2 style={{ marginBottom: '8px' }}>QR Code Created!</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
            Your QR code is ready to use
          </p>
          
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: 'var(--radius-lg)',
            marginBottom: '24px',
            display: 'inline-block'
          }}>
            {createdQR.qrImageUrl ? (
              <img src={createdQR.qrImageUrl} alt="QR Code" style={{ maxWidth: '240px' }} />
            ) : (
              <QRCodeSVG value={createdQR.shortUrl} size={240} />
            )}
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              color: 'var(--text-muted)', 
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '8px'
            }}>Short URL</label>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              background: 'var(--bg-elevated)',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-color)'
            }}>
              <code style={{ flex: 1, fontSize: '14px', wordBreak: 'break-all' }}>
                {createdQR.shortUrl}
              </code>
              <button 
                onClick={() => navigator.clipboard.writeText(createdQR.shortUrl)}
                className="btn btn-ghost"
                style={{ padding: '6px 12px', fontSize: '13px' }}
              >
                Copy
              </button>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={`/qr-codes/view/${createdQR.id}`} className="btn btn-primary">
              View Details
            </Link>
            <button
              onClick={() => {
                setCreatedQR(null);
              }}
              className="btn btn-secondary"
            >
              Create Another
            </button>
            <Link to="/qr-codes" className="btn btn-ghost">
              View All QR Codes
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 style={{ 
          background: 'var(--gradient-primary)', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent' 
        }}>
          Create QR Code
        </h1>
        <Link to="/qr-codes" className="btn btn-secondary">
          ← Back to QR Codes
        </Link>
      </div>

      <div style={{ maxWidth: '700px' }}>
        <div className="card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Destination URL *</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My QR Code"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for your QR code..."
                rows={3}
              />
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="btn btn-ghost"
              style={{ marginBottom: '24px', width: '100%', justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                Smart Redirect Settings
              </span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {showAdvanced && (
              <div style={{ 
                padding: '24px', 
                background: 'var(--bg-elevated)', 
                borderRadius: 'var(--radius-md)',
                marginBottom: '24px',
                border: '1px solid var(--border-color)'
              }}>
                <h3 style={{ marginBottom: '20px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                  iOS Settings
                </h3>
                <div className="form-group">
                  <label>iOS Deep Link</label>
                  <input
                    type="text"
                    value={iosDeepLink}
                    onChange={(e) => setIosDeepLink(e.target.value)}
                    placeholder="myapp://path/to/content"
                  />
                </div>
                <div className="form-group">
                  <label>iOS App Store URL</label>
                  <input
                    type="url"
                    value={iosAppStoreUrl}
                    onChange={(e) => setIosAppStoreUrl(e.target.value)}
                    placeholder="https://apps.apple.com/app/..."
                  />
                </div>

                <h3 style={{ marginBottom: '20px', marginTop: '28px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-tertiary)" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                  Android Settings
                </h3>
                <div className="form-group">
                  <label>Android Deep Link</label>
                  <input
                    type="text"
                    value={androidDeepLink}
                    onChange={(e) => setAndroidDeepLink(e.target.value)}
                    placeholder="myapp://path/to/content"
                  />
                </div>
                <div className="form-group">
                  <label>Play Store URL</label>
                  <input
                    type="url"
                    value={androidPlayStoreUrl}
                    onChange={(e) => setAndroidPlayStoreUrl(e.target.value)}
                    placeholder="https://play.google.com/store/apps/..."
                  />
                </div>

                <h3 style={{ marginBottom: '20px', marginTop: '28px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-secondary)" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  Desktop Settings
                </h3>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Web URL (Desktop)</label>
                  <input
                    type="url"
                    value={webUrl}
                    onChange={(e) => setWebUrl(e.target.value)}
                    placeholder="https://example.com/desktop"
                  />
                </div>
              </div>
            )}

            {error && <div className="error">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ width: '100%', padding: '16px' }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                      <animate attributeName="stroke-dashoffset" dur="1s" repeatCount="indefinite" values="32;0"/>
                    </circle>
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/>
                    <rect x="14" y="3" width="7" height="7"/>
                    <rect x="14" y="14" width="7" height="7"/>
                    <rect x="3" y="14" width="7" height="7"/>
                  </svg>
                  Create QR Code
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateQRCode;
